import { useState, useMemo } from 'react';
import { 
  LineChart, 
  Line, 
  XAxis, 
  YAxis, 
  CartesianGrid, 
  Tooltip, 
  Legend, 
  ResponsiveContainer,
  ReferenceLine,
  Area,
  AreaChart
} from 'recharts';
import { 
  ChartLine, 
  TrendUp, 
  TrendDown, 
  Minus,
  CaretDown,
  Warning,
  CheckCircle
} from '@phosphor-icons/react';

// Common lab metrics to track with their normal ranges
const LAB_METRICS = {
  // Metabolic Panel
  'Glucose': { unit: 'mg/dL', low: 70, high: 100, category: 'Metabolic' },
  'Hemoglobin A1c': { unit: '%', low: 4.0, high: 5.6, category: 'Metabolic' },
  'HbA1c': { unit: '%', low: 4.0, high: 5.6, category: 'Metabolic' },
  'A1C': { unit: '%', low: 4.0, high: 5.6, category: 'Metabolic' },
  'BUN': { unit: 'mg/dL', low: 7, high: 20, category: 'Kidney' },
  'Creatinine': { unit: 'mg/dL', low: 0.7, high: 1.3, category: 'Kidney' },
  'eGFR': { unit: 'mL/min', low: 90, high: 120, category: 'Kidney' },
  
  // Lipid Panel
  'Total Cholesterol': { unit: 'mg/dL', low: 0, high: 200, category: 'Lipid' },
  'Cholesterol': { unit: 'mg/dL', low: 0, high: 200, category: 'Lipid' },
  'LDL': { unit: 'mg/dL', low: 0, high: 100, category: 'Lipid' },
  'LDL Cholesterol': { unit: 'mg/dL', low: 0, high: 100, category: 'Lipid' },
  'HDL': { unit: 'mg/dL', low: 40, high: 60, category: 'Lipid' },
  'HDL Cholesterol': { unit: 'mg/dL', low: 40, high: 60, category: 'Lipid' },
  'Triglycerides': { unit: 'mg/dL', low: 0, high: 150, category: 'Lipid' },
  
  // Liver
  'ALT': { unit: 'U/L', low: 7, high: 56, category: 'Liver' },
  'AST': { unit: 'U/L', low: 10, high: 40, category: 'Liver' },
  'Alkaline Phosphatase': { unit: 'U/L', low: 44, high: 147, category: 'Liver' },
  'Bilirubin': { unit: 'mg/dL', low: 0.1, high: 1.2, category: 'Liver' },
  
  // CBC
  'Hemoglobin': { unit: 'g/dL', low: 12, high: 17.5, category: 'CBC' },
  'Hematocrit': { unit: '%', low: 36, high: 50, category: 'CBC' },
  'WBC': { unit: 'K/uL', low: 4.5, high: 11, category: 'CBC' },
  'White Blood Cell': { unit: 'K/uL', low: 4.5, high: 11, category: 'CBC' },
  'Platelets': { unit: 'K/uL', low: 150, high: 400, category: 'CBC' },
  'RBC': { unit: 'M/uL', low: 4.5, high: 5.5, category: 'CBC' },
  
  // Thyroid
  'TSH': { unit: 'mIU/L', low: 0.4, high: 4.0, category: 'Thyroid' },
  'T4': { unit: 'ug/dL', low: 4.5, high: 12, category: 'Thyroid' },
  'T3': { unit: 'ng/dL', low: 80, high: 200, category: 'Thyroid' },
  
  // Electrolytes
  'Sodium': { unit: 'mEq/L', low: 136, high: 145, category: 'Electrolytes' },
  'Potassium': { unit: 'mEq/L', low: 3.5, high: 5.0, category: 'Electrolytes' },
  'Chloride': { unit: 'mEq/L', low: 98, high: 106, category: 'Electrolytes' },
  'CO2': { unit: 'mEq/L', low: 23, high: 29, category: 'Electrolytes' },
  'Calcium': { unit: 'mg/dL', low: 8.5, high: 10.5, category: 'Electrolytes' },
};

const CATEGORY_COLORS = {
  'Metabolic': '#14b8a6',
  'Kidney': '#8b5cf6',
  'Lipid': '#f59e0b',
  'Liver': '#ef4444',
  'CBC': '#3b82f6',
  'Thyroid': '#ec4899',
  'Electrolytes': '#22c55e',
};

const CustomTooltip = ({ active, payload, label }) => {
  if (active && payload && payload.length) {
    const data = payload[0].payload;
    const metric = LAB_METRICS[payload[0].name] || {};
    const value = payload[0].value;
    const isHigh = metric.high && value > metric.high;
    const isLow = metric.low && value < metric.low;
    
    return (
      <div className="bg-white p-3 rounded-lg shadow-lg border border-slate-200">
        <p className="text-sm font-medium text-slate-900">{label}</p>
        <p className={`text-lg font-bold ${isHigh ? 'text-red-600' : isLow ? 'text-blue-600' : 'text-green-600'}`}>
          {value} {metric.unit || ''}
        </p>
        {metric.low !== undefined && metric.high !== undefined && (
          <p className="text-xs text-slate-500">
            Normal: {metric.low} - {metric.high} {metric.unit}
          </p>
        )}
        {(isHigh || isLow) && (
          <p className={`text-xs font-medium mt-1 ${isHigh ? 'text-red-600' : 'text-blue-600'}`}>
            {isHigh ? 'Above normal range' : 'Below normal range'}
          </p>
        )}
      </div>
    );
  }
  return null;
};

export default function LabTrendsChart({ labResults, patientName }) {
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [selectedMetric, setSelectedMetric] = useState(null);
  
  // Process lab results to extract time series data for each metric
  const { trendData, availableMetrics, categories } = useMemo(() => {
    const metricsMap = new Map();
    const categoriesSet = new Set();
    
    // Sort labs by date
    const sortedLabs = [...labResults].sort((a, b) => {
      const dateA = a.collection_date ? new Date(a.collection_date) : new Date(a.created_at);
      const dateB = b.collection_date ? new Date(b.collection_date) : new Date(b.created_at);
      return dateA - dateB;
    });
    
    // Extract metrics from each lab
    sortedLabs.forEach(lab => {
      const date = lab.collection_date || lab.created_at?.split('T')[0] || 'Unknown';
      const formattedDate = new Date(date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: '2-digit' });
      
      (lab.results || []).forEach(result => {
        const testName = result.test_name;
        const value = parseFloat(result.value);
        
        if (isNaN(value)) return;
        
        // Find matching metric config
        const metricConfig = LAB_METRICS[testName];
        const category = metricConfig?.category || 'Other';
        categoriesSet.add(category);
        
        if (!metricsMap.has(testName)) {
          metricsMap.set(testName, {
            name: testName,
            category,
            data: [],
            unit: result.unit || metricConfig?.unit || '',
            low: metricConfig?.low,
            high: metricConfig?.high,
          });
        }
        
        metricsMap.get(testName).data.push({
          date: formattedDate,
          value,
          flag: result.flag,
          fullDate: date,
        });
      });
    });
    
    // Convert to array and calculate trends
    const metrics = Array.from(metricsMap.values()).map(metric => {
      const values = metric.data.map(d => d.value);
      let trend = 'stable';
      if (values.length >= 2) {
        const first = values[0];
        const last = values[values.length - 1];
        const change = ((last - first) / first) * 100;
        if (change > 10) trend = 'increasing';
        else if (change < -10) trend = 'decreasing';
      }
      
      // Check if latest value is out of range
      const latestValue = values[values.length - 1];
      const isAbnormal = (metric.high && latestValue > metric.high) || (metric.low && latestValue < metric.low);
      
      return { ...metric, trend, isAbnormal, latestValue };
    });
    
    return {
      trendData: metrics,
      availableMetrics: metrics.map(m => m.name),
      categories: Array.from(categoriesSet),
    };
  }, [labResults]);
  
  // Filter metrics by category
  const filteredMetrics = useMemo(() => {
    if (selectedCategory === 'all') return trendData;
    return trendData.filter(m => m.category === selectedCategory);
  }, [trendData, selectedCategory]);
  
  // Get data for selected metric chart
  const chartData = useMemo(() => {
    if (!selectedMetric) return null;
    const metric = trendData.find(m => m.name === selectedMetric);
    return metric;
  }, [selectedMetric, trendData]);
  
  if (labResults.length === 0) {
    return (
      <div className="card-clinical p-6">
        <div className="flex items-center gap-2 mb-4">
          <ChartLine size={20} className="text-teal-500" />
          <h2 className="text-lg font-semibold text-slate-900">Lab Trends</h2>
        </div>
        <div className="p-8 bg-slate-50 rounded-xl text-center">
          <ChartLine size={48} className="text-slate-300 mx-auto mb-4" />
          <p className="text-slate-500">No lab results to display trends</p>
          <p className="text-sm text-slate-400 mt-1">Add lab results to see trends over time</p>
        </div>
      </div>
    );
  }
  
  return (
    <div className="card-clinical p-6" data-testid="lab-trends-chart">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <ChartLine size={20} className="text-teal-500" />
          <h2 className="text-lg font-semibold text-slate-900">Lab Trends</h2>
        </div>
        
        {/* Category Filter */}
        <div className="relative">
          <select
            value={selectedCategory}
            onChange={(e) => { setSelectedCategory(e.target.value); setSelectedMetric(null); }}
            className="input-clinical pr-8 text-sm"
            data-testid="category-filter"
          >
            <option value="all">All Categories</option>
            {categories.map(cat => (
              <option key={cat} value={cat}>{cat}</option>
            ))}
          </select>
        </div>
      </div>
      
      {/* Metrics Grid */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3 mb-6">
        {filteredMetrics.map(metric => (
          <button
            key={metric.name}
            onClick={() => setSelectedMetric(selectedMetric === metric.name ? null : metric.name)}
            className={`p-3 rounded-xl border transition-all text-left ${
              selectedMetric === metric.name 
                ? 'border-teal-500 bg-teal-50 ring-2 ring-teal-500/20' 
                : metric.isAbnormal 
                  ? 'border-red-200 bg-red-50 hover:border-red-300'
                  : 'border-slate-200 bg-slate-50 hover:border-slate-300'
            }`}
            data-testid={`metric-btn-${metric.name.replace(/\s+/g, '-')}`}
          >
            <div className="flex items-center justify-between mb-1">
              <span className="text-xs font-medium text-slate-500 truncate" style={{ color: CATEGORY_COLORS[metric.category] }}>
                {metric.category}
              </span>
              {metric.trend === 'increasing' ? (
                <TrendUp size={14} className={metric.isAbnormal ? 'text-red-500' : 'text-slate-400'} />
              ) : metric.trend === 'decreasing' ? (
                <TrendDown size={14} className={metric.isAbnormal ? 'text-blue-500' : 'text-slate-400'} />
              ) : (
                <Minus size={14} className="text-slate-400" />
              )}
            </div>
            <p className="text-sm font-medium text-slate-900 truncate">{metric.name}</p>
            <div className="flex items-center gap-1 mt-1">
              <span className={`text-lg font-bold ${metric.isAbnormal ? 'text-red-600' : 'text-slate-900'}`}>
                {metric.latestValue}
              </span>
              <span className="text-xs text-slate-500">{metric.unit}</span>
              {metric.isAbnormal && (
                <Warning size={14} className="text-red-500 ml-1" />
              )}
            </div>
            <p className="text-xs text-slate-400 mt-1">{metric.data.length} result{metric.data.length > 1 ? 's' : ''}</p>
          </button>
        ))}
      </div>
      
      {/* Selected Metric Chart */}
      {chartData && (
        <div className="border border-slate-200 rounded-xl p-4 bg-white">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h3 className="font-semibold text-slate-900">{chartData.name}</h3>
              <p className="text-sm text-slate-500">
                {chartData.low !== undefined && chartData.high !== undefined 
                  ? `Normal range: ${chartData.low} - ${chartData.high} ${chartData.unit}`
                  : chartData.unit}
              </p>
            </div>
            <div className="flex items-center gap-2">
              {chartData.trend === 'increasing' && (
                <span className="text-xs bg-amber-100 text-amber-700 px-2 py-1 rounded-full flex items-center gap-1">
                  <TrendUp size={12} /> Increasing
                </span>
              )}
              {chartData.trend === 'decreasing' && (
                <span className="text-xs bg-blue-100 text-blue-700 px-2 py-1 rounded-full flex items-center gap-1">
                  <TrendDown size={12} /> Decreasing
                </span>
              )}
              {chartData.trend === 'stable' && (
                <span className="text-xs bg-green-100 text-green-700 px-2 py-1 rounded-full flex items-center gap-1">
                  <CheckCircle size={12} /> Stable
                </span>
              )}
            </div>
          </div>
          
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={chartData.data} margin={{ top: 10, right: 30, left: 0, bottom: 0 }}>
                <defs>
                  <linearGradient id="colorValue" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#14b8a6" stopOpacity={0.3}/>
                    <stop offset="95%" stopColor="#14b8a6" stopOpacity={0}/>
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                <XAxis 
                  dataKey="date" 
                  tick={{ fontSize: 12, fill: '#64748b' }}
                  tickLine={false}
                  axisLine={{ stroke: '#e2e8f0' }}
                />
                <YAxis 
                  tick={{ fontSize: 12, fill: '#64748b' }}
                  tickLine={false}
                  axisLine={{ stroke: '#e2e8f0' }}
                  domain={['auto', 'auto']}
                />
                <Tooltip content={<CustomTooltip />} />
                
                {/* Reference lines for normal range */}
                {chartData.high !== undefined && (
                  <ReferenceLine 
                    y={chartData.high} 
                    stroke="#ef4444" 
                    strokeDasharray="5 5" 
                    label={{ value: 'High', position: 'right', fill: '#ef4444', fontSize: 11 }}
                  />
                )}
                {chartData.low !== undefined && chartData.low > 0 && (
                  <ReferenceLine 
                    y={chartData.low} 
                    stroke="#3b82f6" 
                    strokeDasharray="5 5" 
                    label={{ value: 'Low', position: 'right', fill: '#3b82f6', fontSize: 11 }}
                  />
                )}
                
                <Area 
                  type="monotone" 
                  dataKey="value" 
                  name={chartData.name}
                  stroke="#14b8a6" 
                  strokeWidth={2}
                  fill="url(#colorValue)"
                  dot={{ fill: '#14b8a6', strokeWidth: 2, r: 4 }}
                  activeDot={{ r: 6, fill: '#14b8a6' }}
                />
              </AreaChart>
            </ResponsiveContainer>
          </div>
          
          {/* Data Table */}
          <div className="mt-4 overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-slate-200">
                  <th className="text-left py-2 text-slate-600 font-medium">Date</th>
                  <th className="text-left py-2 text-slate-600 font-medium">Value</th>
                  <th className="text-left py-2 text-slate-600 font-medium">Status</th>
                </tr>
              </thead>
              <tbody>
                {[...chartData.data].reverse().map((point) => {
                  const isHigh = chartData.high && point.value > chartData.high;
                  const isLow = chartData.low && point.value < chartData.low;
                  return (
                    <tr key={`point-${point.date}-${point.value}`} className="border-b border-slate-100">
                      <td className="py-2 text-slate-700">{point.date}</td>
                      <td className={`py-2 font-medium ${isHigh ? 'text-red-600' : isLow ? 'text-blue-600' : 'text-slate-900'}`}>
                        {point.value} {chartData.unit}
                      </td>
                      <td className="py-2">
                        {isHigh ? (
                          <span className="text-xs bg-red-100 text-red-700 px-2 py-0.5 rounded">High</span>
                        ) : isLow ? (
                          <span className="text-xs bg-blue-100 text-blue-700 px-2 py-0.5 rounded">Low</span>
                        ) : (
                          <span className="text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded">Normal</span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}
      
      {!selectedMetric && filteredMetrics.length > 0 && (
        <div className="text-center p-4 bg-slate-50 rounded-xl">
          <p className="text-slate-500 text-sm">Click on a metric above to view its trend chart</p>
        </div>
      )}
      
      {filteredMetrics.length === 0 && (
        <div className="text-center p-4 bg-slate-50 rounded-xl">
          <p className="text-slate-500 text-sm">No metrics found for the selected category</p>
        </div>
      )}
    </div>
  );
}
